// features/admin/UserList.tsx
//
// Admin users page rebuilt around a usable role system. The earlier
// version was a read-only table — no filter, no edit, no way to
// change a user's role. This page adds:
//
//   • Live role filter pulled from /admin/roles/assignable, so the
//     options match the actual seeded set (not the legacy hardcoded
//     three).
//   • Status pill that doubles as an activate/deactivate toggle.
//   • Edit modal with role dropdown + activate flag + email/phone +
//     optional password reset, all running against the now-fixed PUT
//     /admin/users/admins/:id endpoint (validators previously
//     required Mongo ObjectIds and silently rejected every call).
//   • Search by name/email + pagination — the backend already
//     supported both, only the UI was missing.
//   • Last-seen badge so an operator can tell who's been around
//     recently (sourced from AdminUser.lastSeenAt heartbeat that also
//     powers the Krushi Doctor online pill).
import { useEffect, useState, useCallback } from 'react';
import { FiUsers, FiSearch, FiRefreshCw, FiEdit2, FiX, FiAlertCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';

import {
  AdminUserListItem,
  AdminUserPayload,
  getAdminUsers,
  updateAdminUser,
  deleteAdminUser,
} from '../auth/api';
import { rolesApi, AssignableRole } from '../roles/api';
import { Card } from '../../components/common/Card';

export default function UserList() {
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<AssignableRole[]>([]);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Edit modal
  const [editing, setEditing] = useState<AdminUserListItem | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminUsers({
        page,
        limit: 20,
        search: search || undefined,
        role: roleFilter || undefined,
        status: statusFilter,
      });
      setUsers(res.items);
      if (res.pagination) {
        setTotalPages(res.pagination.totalPages || 1);
        setTotal(res.pagination.total || res.items.length);
      } else {
        setTotalPages(1);
        setTotal(res.items.length);
      }
    } catch {
      toast.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, statusFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    rolesApi.assignable().then(setRoles).catch(() => {/* soft fail */});
  }, []);

  const handleSubmitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const toggleActive = async (user: AdminUserListItem) => {
    try {
      await updateAdminUser(user.id, { isActive: !user.isActive });
      toast.success(user.isActive ? 'Admin deactivated' : 'Admin activated');
      fetchUsers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDelete = async (user: AdminUserListItem) => {
    if (!window.confirm(`Deactivate ${user.firstName} ${user.lastName}? They won't be able to log in.`)) return;
    try {
      await deleteAdminUser(user.id);
      toast.success('Admin deactivated');
      fetchUsers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to deactivate');
    }
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <FiUsers size={20} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Admin Users</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total} registered admin{total === 1 ? '' : 's'}{roleFilter ? ` · filtered by ${roleFilter}` : ''}
            </p>
          </div>
        </div>
        <button
          onClick={fetchUsers}
          className="self-start sm:self-auto flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-slate-700 dark:text-slate-300"
        >
          <FiRefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-4 flex flex-wrap items-end gap-3">
        <form onSubmit={handleSubmitSearch} className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Search</label>
          <div className="relative">
            <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Name, email, phone..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </form>
        <div>
          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Role</label>
          <select
            value={roleFilter}
            onChange={(e) => { setPage(1); setRoleFilter(e.target.value); }}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All roles</option>
            {roles.map((r) => (
              <option key={r.id} value={r.name}>{r.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => { setPage(1); setStatusFilter(e.target.value as any); }}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        {(search || roleFilter || statusFilter !== 'all') && (
          <button
            onClick={() => { setSearchInput(''); setSearch(''); setRoleFilter(''); setStatusFilter('all'); setPage(1); }}
            className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
          >
            Reset
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <Card className="p-12">
          <div className="flex justify-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </Card>
      ) : users.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <FiUsers className="mx-auto text-4xl text-slate-400 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No admins match these filters</h3>
            <p className="text-slate-500 dark:text-slate-400">Try widening the search or reset the filter.</p>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Last seen</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 dark:text-white">
                        {user.firstName} {user.lastName}
                      </p>
                      {user.phone && (
                        <p className="text-xs text-slate-400">{user.phone}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">{user.email}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border border-violet-200 dark:border-violet-800">
                        {user.role?.name ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {relativeTime(user.lastSeenAt ?? user.lastLoginAt ?? null)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleActive(user)}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                          user.isActive
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 hover:bg-emerald-200'
                            : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400 hover:bg-slate-200'
                        }`}
                        title="Click to toggle"
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setEditing(user)}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                          title="Edit admin"
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="px-2 py-1 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          Deactivate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Page {page} of {totalPages} · {total} total
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Edit modal */}
      {editing && (
        <EditAdminModal
          user={editing}
          roles={roles}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchUsers(); }}
        />
      )}
    </div>
  );
}

// ── Inline edit modal ────────────────────────────────────────────────
const EditAdminModal: React.FC<{
  user: AdminUserListItem;
  roles: AssignableRole[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ user, roles, onClose, onSaved }) => {
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone ?? '');
  const [roleId, setRoleId] = useState<number | null>(user.role?.id ?? null);
  const [isActive, setIsActive] = useState(user.isActive);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const payload: AdminUserPayload = {
      firstName: firstName.trim() !== user.firstName ? firstName.trim() : undefined,
      lastName: lastName.trim() !== user.lastName ? lastName.trim() : undefined,
      email: email.trim().toLowerCase() !== user.email ? email.trim().toLowerCase() : undefined,
      phone: phone.trim() !== (user.phone ?? '') ? phone.trim() : undefined,
      roleId: roleId !== user.role?.id ? roleId ?? undefined : undefined,
      isActive: isActive !== user.isActive ? isActive : undefined,
      password: password.trim() ? password : undefined,
    };
    // Drop undefined keys so we only send what changed (smaller diff,
    // also avoids the backend re-hashing the password on no-op saves).
    Object.keys(payload).forEach((k) => {
      if ((payload as any)[k] === undefined) delete (payload as any)[k];
    });
    if (Object.keys(payload).length === 0) {
      toast('No changes to save');
      setBusy(false);
      return;
    }
    try {
      await updateAdminUser(user.id, payload);
      toast.success('Admin updated');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800 dark:text-white">Edit Admin</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <FiX size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </Field>
            <Field label="Last name">
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </Field>
          </div>
          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </Field>
          <Field label="Phone">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </Field>
          <Field label="Role">
            <select
              value={roleId ?? ''}
              onChange={(e) => setRoleId(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">— pick a role —</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.name}{r.description ? ` · ${r.description}` : ''}</option>
              ))}
            </select>
          </Field>
          <Field
            label="Reset password (optional)"
            help="Leave blank to keep the existing password. Min 8 chars + upper/lower/number/symbol."
          >
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password"
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            Active (can log in)
          </label>
          <div className="text-[11px] text-slate-400 flex items-start gap-1">
            <FiAlertCircle size={12} className="mt-0.5 flex-shrink-0" />
            <span>Role changes take effect on the admin's next request — they may need to refresh.</span>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex gap-3">
          <button
            onClick={submit}
            disabled={busy}
            className="flex-1 py-2.5 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-60 transition-colors"
          >
            {busy ? 'Saving...' : 'Save changes'}
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; help?: string; children: React.ReactNode }> = ({ label, help, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">{label}</label>
    {children}
    {help && <p className="text-[11px] text-slate-400 mt-1">{help}</p>}
  </div>
);

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '—';
  const diffMin = Math.round((Date.now() - t.getTime()) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return t.toLocaleDateString();
}
